import { useEffect, useRef } from 'react';

/*
 * Focus for a panel that opens in place of the control that opened it.
 *
 * An empty list's "add the first one" button disappears as the create form
 * appears, and the form usually renders above where the button was. Left
 * alone, focus falls to <body> and a keyboard or screen-reader user has to
 * find the form again from the top of the page. This moves focus to the
 * panel each time `open` turns true.
 *
 *   const headingRef = useFocusOnOpen(showForm);
 *   <h3 ref={headingRef} tabIndex={-1}>...</h3>
 *
 * Put the ref on the panel's heading with tabIndex={-1}: the heading names
 * what just opened, and Tab goes on to the first field from there. Nothing
 * happens on the first render, so a page that mounts with a panel open does
 * not steal focus.
 */
export function focusElement(el) {
  if (!el || typeof el.focus !== 'function') return false;
  el.focus({ preventScroll: true });
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  return true;
}

export default function useFocusOnOpen(open) {
  const ref = useRef(null);
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) focusElement(ref.current);
    wasOpen.current = open;
  }, [open]);
  return ref;
}
